/**
 * Paddle sends amounts as integer strings in the currency's minor unit
 * (e.g. USD cents), except for currencies that have no minor unit at all.
 * For those, the amount Paddle sends is already the full, displayable value
 * and must not be divided by 100. This is Paddle's complete zero-decimal
 * set (decimal_places: 0 for every one of their 33 supported currencies) —
 * https://developer.paddle.com/concepts/sell/supported-currencies/
 */
const ZERO_DECIMAL_CURRENCIES = new Set(["CLP", "JPY", "KRW", "VND"]);

export const isZeroDecimalCurrency = (currencyCode: string): boolean =>
  ZERO_DECIMAL_CURRENCIES.has(currencyCode.trim().toUpperCase());

/** Converts a raw Paddle `PricePreview`/transaction amount into a display-ready number. */
export const parsePaddleAmount = (
  amount: string | number,
  currencyCode: string
): number => {
  const numericAmount =
    typeof amount === "number" ? amount : parseInt(amount, 10);
  return isZeroDecimalCurrency(currencyCode)
    ? numericAmount
    : numericAmount / 100;
};

/**
 * Resolves the currency code to use for a Paddle amount, defaulting to USD
 * only while the response hasn't loaded yet. If the response HAS loaded but
 * is missing its currency code, that's a Paddle API contract violation, not
 * a normal loading state — silently defaulting to USD there would re-hide
 * the zero-decimal bug (CLP/JPY/KRW/VND amounts would divide by 100 again), so
 * this logs loudly instead of failing quietly.
 */
export const resolvePaddleCurrencyCode = (
  hasData: boolean,
  currencyCode: string | undefined
): string => {
  if (!hasData) return "USD";
  if (!currencyCode) {
    console.error(
      "Paddle response loaded without a currency code; defaulting to USD. " +
        "This can mis-price zero-decimal currencies (CLP/JPY/KRW/VND)."
    );
    return "USD";
  }
  return currencyCode;
};
